/**
 * @prettier
 */
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@pidedirecto/ui';
import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { signUpApi } from 'src/api/pidedirecto/customer/signUpApi';
import { GetAppContextSignedInApiResponse } from 'src/api/pidedirecto/getAppContextSignedInApi';
import { Dialog } from 'src/components/Dialog';
import { DialogActions } from 'src/components/DialogActions';
import { Form } from 'src/components/form/Form';
import { FormEmailField } from 'src/components/form/FormEmailField';
import { FormPasswordField } from 'src/components/form/FormPasswordField';
import { FormTextField } from 'src/components/form/FormTextField';
import { Text } from 'src/components/Text';
import { translate } from 'src/i18n/translate';
import { actions } from 'src/reducers';
import { useGetAppContext } from 'src/services/useGetAppContext';
import { alertKnownErrorOrSomethingWentWrong } from 'src/utils/alert/alertKnownErrorOrSomethingWentWrong';
import { encryptDataInTransit } from 'src/utils/crypto/encryptDataInTransit';
import { useAction } from 'src/utils/react/useAction';
import { useSelector } from 'src/utils/react/useSelector';
import { getUrlSubdomain } from 'src/utils/url/getUrlSubdomain';

export function SignUpDialog(): React.ReactElement {
    const form = useForm();
    const classes = useStyles();
    const { setError } = form;

    const { getAppContextSignedIn } = useGetAppContext();

    const [loading, setLoading] = useState(false);

    const restaurant = useSelector((state) => state.app.restaurant);
    const open = useSelector((state) => state.app.signUpDialog.open);
    const mobileNumber = useSelector((state) => state.app.signUpDialog.mobileNumber);
    const onSuccess = useSelector((state) => state.app.signUpDialog.onSuccess);

    const closeSignUpDialog = useAction(actions.closeSignUpDialog);

    const onSubmit = async (form: any) => {
        setLoading(true);
        if (form.password !== form.confirmPassword) {
            setError('confirmPassword', { message: translate('Password does not match') });
            return;
        }

        const response = await signUpApi({
            mobileNumber,
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            encryptedPassword: encryptDataInTransit(form.password),
            urlSubdomain: getUrlSubdomain(),
        });
        setLoading(false);
        if (!response.ok) {
            alertKnownErrorOrSomethingWentWrong(response);
            return;
        }
        await fetchAppContextRecursive();
    };

    const fetchAppContextRecursive = async () => {
        const appContextResponse = await getAppContextSignedIn({ urlSubdomain: getUrlSubdomain(), urlPathname: restaurant.urlPathname ?? '' });

        if (!appContextResponse.ok) {
            alert(translate('Failed to sign in, check you internet connection and press ok to retry'));
            fetchAppContextRecursive();
            return;
        }

        const appContext: GetAppContextSignedInApiResponse = appContextResponse.data as any;

        closeSignUpDialog();
        onSuccess?.(appContext);
    };

    return (
        <Dialog open={open} title={translate('Save time, sign up now!')} loading={loading}>
            <Text className={classes.text}>{translate('Your next visit will be simpler and faster, you only have to create a password')}</Text>
            <Form form={form} className={classes.form} onSubmit={onSubmit}>
                <FormTextField name='firstName' label={translate('First Name')} disabled={loading} />
                <FormTextField name='lastName' label={translate('Last Name')} disabled={loading} />
                <FormTextField name='username' defaultValue={mobileNumber} label={translate('Phone')} disabled />
                <FormEmailField name='email' label={translate('Email')} disabled={loading} />
                <FormPasswordField name='password' label={translate('Password')} required disabled={loading} />
                <FormPasswordField name='confirmPassword' label={translate('Confirm password')} required disabled={loading} />
                <DialogActions>
                    <Button variant={'secondary'} onClick={closeSignUpDialog} classes={{ button: classes.buttons }} disabled={loading}>
                        {translate('At another time')}
                    </Button>
                    <Button type='submit' classes={{ button: classes.buttons }} disabled={loading}>
                        {translate('Sign up')}
                    </Button>
                </DialogActions>
            </Form>
        </Dialog>
    );
}

const useStyles = makeStyles((theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    text: {
        textAlign: 'center',
        marginBottom: 10,
    },
    buttons: {
        width: '49%',
    },
}));
